import { useState, useEffect, useMemo } from 'react';
import { Send, Calendar, Users, User, MapPin, Phone, Mail, MessageSquare, Car, Plane, ArrowLeftRight, ArrowRight, CheckCircle, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { SearchableSelect } from './SearchableSelect';
import { toast } from 'sonner';
import { createReservation, calculateRoutePrice } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useData } from '../contexts/DataContext';

export default function BookingForm() {
  const { airports, destinations, hotels, vehicles, excursions, heroPreFill, setHeroPreFill } = useData();
  const [loading, setLoading] = useState(false);
  const [reservationType, setReservationType] = useState('transfer');
  const [tripType, setTripType] = useState('one_way');
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [whatsappUrl, setWhatsappUrl] = useState(null);
  const { t, lang } = useLang();
  
  const [formData, setFormData] = useState({
    name: '', email: '', whatsapp: '', pickup_location: '', destination: '',
    origin_id: '', origin_type: '', zone_id: '', hotel_id: '', excursion_id: '',
    date: '', time: '', return_date: '', return_time: '',
    passengers: '2', vehicle_id: '', comments: ''
  });

  useEffect(() => {
    if (heroPreFill) {
      setFormData(prev => ({
        ...prev,
        origin_id: heroPreFill.origin_id || '',
        origin_type: heroPreFill.origin_type || '',
        zone_id: heroPreFill.destination_id || '',
        date: heroPreFill.date || '',
        passengers: heroPreFill.passengers || '2'
      }));
      setReservationType('transfer');
      setHeroPreFill(null);
    }
  }, [heroPreFill, setHeroPreFill]);

  // Build hotel→zone lookup
  const hotelZoneMap = useMemo(() => {
    const map = {};
    hotels.forEach(h => { map[h.id] = h.zone_id; });
    return map;
  }, [hotels]);

  useEffect(() => {
    const fetchPrice = async () => {
      if (formData.origin_id && formData.zone_id && reservationType === 'transfer') {
        try {
          // If origin is a hotel, resolve to its zone for price lookup
          let originZoneId = formData.origin_id;
          if (formData.origin_type === 'hotel') {
            originZoneId = hotelZoneMap[formData.origin_id] || formData.origin_id;
          }
          const response = await calculateRoutePrice(originZoneId, formData.zone_id);
          const basePrice = response.data.price;
          setEstimatedPrice(tripType === 'round_trip' ? basePrice * 2 : basePrice);
        } catch (error) { setEstimatedPrice(null); }
      } else if (reservationType === 'excursion' && formData.excursion_id) {
        const excursion = excursions.find(e => e.id === formData.excursion_id);
        setEstimatedPrice(excursion?.price || null);
      } else { setEstimatedPrice(null); }
    };
    fetchPrice();
  }, [formData.origin_id, formData.origin_type, formData.zone_id, formData.excursion_id, reservationType, tripType, excursions, hotelZoneMap]);

  const filteredHotels = formData.zone_id ? hotels.filter(h => h.zone_id === formData.zone_id) : hotels;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const origin = formData.origin_type === 'hotel'
        ? hotels.find(h => h.id === formData.origin_id)
        : airports.find(a => a.id === formData.origin_id);
      const originName = origin?.name || formData.pickup_location;
      const destination = destinations.find(d => d.id === formData.zone_id);
      const excursion = excursions.find(ex => ex.id === formData.excursion_id);
      const hotel = hotels.find(h => h.id === formData.hotel_id);
      const vehicle = vehicles.find(v => v.id === formData.vehicle_id);

      await createReservation({
        ...formData,
        passengers: parseInt(formData.passengers),
        reservation_type: reservationType,
        trip_type: reservationType === 'transfer' ? tripType : 'one_way',
        return_date: tripType === 'round_trip' ? formData.return_date : null,
        return_time: tripType === 'round_trip' ? formData.return_time : null,
        pickup_location: originName,
        destination: destination?.name || excursion?.name || ''
      });
      
      toast.success(t('book_success'));
      
      const tripLabel = tripType === 'round_trip' ? (lang === 'es' ? 'Ida y Vuelta' : 'Round Trip') : (lang === 'es' ? 'Solo Ida' : 'One Way');
      let message = `*${lang === 'es' ? 'Nueva Reservación - Ferr Tours' : 'New Reservation - Ferr Tours'}*\n\n`;
      message += `*${lang === 'es' ? 'Nombre' : 'Name'}:* ${formData.name}\n`;
      message += `*Email:* ${formData.email}\n`;
      message += `*WhatsApp:* ${formData.whatsapp}\n`;
      message += `*${lang === 'es' ? 'Tipo' : 'Type'}:* ${reservationType === 'transfer' ? (lang === 'es' ? 'Traslado' : 'Transfer') : (lang === 'es' ? 'Excursión' : 'Excursion')}\n`;
      if (reservationType === 'transfer') {
        message += `*${lang === 'es' ? 'Modalidad' : 'Trip Type'}:* ${tripLabel}\n`;
        message += `*${lang === 'es' ? 'Desde' : 'From'}:* ${originName}\n`;
        message += `*${lang === 'es' ? 'Hasta' : 'To'}:* ${destination?.name}\n`;
        if (hotel) message += `*Hotel:* ${hotel.name}\n`;
      } else { message += `*${lang === 'es' ? 'Excursión' : 'Excursion'}:* ${excursion?.name}\n`; }
      message += `*${lang === 'es' ? 'Fecha' : 'Date'}:* ${formData.date}\n`;
      message += `*${lang === 'es' ? 'Hora' : 'Time'}:* ${formData.time}\n`;
      if (tripType === 'round_trip' && formData.return_date) {
        message += `*${lang === 'es' ? 'Fecha Regreso' : 'Return Date'}:* ${formData.return_date}\n`;
        if (formData.return_time) message += `*${lang === 'es' ? 'Hora Regreso' : 'Return Time'}:* ${formData.return_time}\n`;
      }
      message += `*${lang === 'es' ? 'Pasajeros' : 'Passengers'}:* ${formData.passengers}\n`;
      if (vehicle) message += `*${lang === 'es' ? 'Vehículo' : 'Vehicle'}:* ${vehicle.name}\n`;
      if (estimatedPrice) message += `\n*${lang === 'es' ? 'Precio Estimado' : 'Estimated Price'}:* $${estimatedPrice} USD\n`;
      if (formData.comments) message += `\n*${lang === 'es' ? 'Comentarios' : 'Comments'}:* ${formData.comments}`;

      const url = `https://wa.me/18299838580?text=${encodeURIComponent(message)}`;
      setWhatsappUrl(url);

      setFormData({ name: '', email: '', whatsapp: '', pickup_location: '', destination: '',
        origin_id: '', origin_type: '', zone_id: '', hotel_id: '', excursion_id: '',
        date: '', time: '', return_date: '', return_time: '',
        passengers: '2', vehicle_id: '', comments: '' });
      setTripType('one_way');
      setEstimatedPrice(null);
    } catch (error) {
      toast.error(t('book_error'));
    } finally { setLoading(false); }
  };

  const originOptions = useMemo(() => {
    const airportLabel = lang === 'es' ? 'Aeropuertos / Puertos' : 'Airports / Ports';
    const hotelLabel = lang === 'es' ? 'Hoteles' : 'Hotels';
    const opts = [];
    opts.push({ isHeader: true, label: airportLabel, value: '__header_airports' });
    airports.forEach(a => opts.push({
      value: `airport_${a.id}`,
      label: lang === 'es' ? (a.name_es || a.name) : a.name,
      group: airportLabel
    }));
    opts.push({ isHeader: true, label: hotelLabel, value: '__header_hotels' });
    hotels.forEach(h => opts.push({
      value: `hotel_${h.id}`,
      label: h.name,
      group: hotelLabel
    }));
    return opts;
  }, [airports, hotels, lang]);

  const destOpts = destinations.map(d => ({ value: d.id, label: lang === 'es' ? (d.name_es || d.name) : d.name }));
  const filteredHotelOpts = filteredHotels.map(h => ({ value: h.id, label: h.name }));

  const handleOriginChange = (val) => {
    if (val.startsWith('airport_')) {
      setFormData({ ...formData, origin_id: val.replace('airport_', ''), origin_type: 'airport' });
    } else if (val.startsWith('hotel_')) {
      setFormData({ ...formData, origin_id: val.replace('hotel_', ''), origin_type: 'hotel' });
    }
  };

  const currentOriginVal = formData.origin_id
    ? (formData.origin_type === 'hotel' ? `hotel_${formData.origin_id}` : `airport_${formData.origin_id}`)
    : '';

  return (
    <section id="booking" className="py-24 bg-[#112240]" data-testid="booking-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-cyan-400 text-sm uppercase tracking-[0.2em] mb-4 font-semibold">{t('book_label')}</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
            {t('book_title_1')} <span className="gradient-text">{t('book_title_2')}</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">{t('book_desc')}</p>
        </div>

        <div className="premium-card p-8">
          {/* Reservation Type Tabs */}
          <div className="flex gap-4 mb-8">
            <button type="button" onClick={() => setReservationType('transfer')}
              className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all ${reservationType === 'transfer' ? 'bg-gradient-to-r from-orange-600 to-yellow-500 text-black' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
              data-testid="tab-transfer">{t('book_transfer')}</button>
            <button type="button" onClick={() => setReservationType('excursion')}
              className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all ${reservationType === 'excursion' ? 'bg-gradient-to-r from-orange-600 to-yellow-500 text-black' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
              data-testid="tab-excursion">{t('book_excursion')}</button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <User className="w-4 h-4 text-cyan-400" />{t('book_name')} *
                </label>
                <Input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white h-12" data-testid="booking-name-input" />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <Mail className="w-4 h-4 text-cyan-400" />{t('book_email')} *
                </label>
                <Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-white/5 border-white/10 text-white h-12" data-testid="booking-email-input" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <Phone className="w-4 h-4 text-orange-400" />{t('book_whatsapp')} *
              </label>
              <Input type="tel" required value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                className="bg-white/5 border-white/10 text-white h-12" placeholder="+1 234 567 8900" data-testid="booking-whatsapp-input" />
            </div>

            {/* Transfer Fields */}
            {reservationType === 'transfer' && (
              <>
                {/* Trip Type Toggle - One Way / Round Trip */}
                <div className="space-y-2">
                  <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                    <ArrowLeftRight className="w-4 h-4 text-cyan-400" />{t('book_trip_type')}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => setTripType('one_way')}
                      className={`py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                        tripType === 'one_way'
                          ? 'bg-cyan-500/20 border-2 border-cyan-500/50 text-cyan-400'
                          : 'bg-white/5 border-2 border-white/10 text-slate-400 hover:bg-white/10'
                      }`}
                      data-testid="trip-one-way"
                    >
                      <ArrowRight className="w-4 h-4" />
                      {t('book_one_way')}
                    </button>
                    <button type="button" onClick={() => setTripType('round_trip')}
                      className={`py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                        tripType === 'round_trip'
                          ? 'bg-orange-500/20 border-2 border-orange-500/50 text-orange-400'
                          : 'bg-white/5 border-2 border-white/10 text-slate-400 hover:bg-white/10'
                      }`}
                      data-testid="trip-round-trip"
                    >
                      <ArrowLeftRight className="w-4 h-4" />
                      {t('book_round_trip')}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                      <Plane className="w-4 h-4 text-cyan-400" />{t('book_pickup_airport')} *
                    </label>
                    <SearchableSelect options={originOptions} value={currentOriginVal}
                      onValueChange={handleOriginChange}
                      placeholder={t('book_select_airport')} searchPlaceholder={t('search_placeholder')}
                      testId="booking-origin-select" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-orange-400" />{t('book_dest_zone')} *
                    </label>
                    <SearchableSelect options={destOpts} value={formData.zone_id}
                      onValueChange={(v) => setFormData({ ...formData, zone_id: v, hotel_id: '' })}
                      placeholder={t('book_select_dest')} searchPlaceholder={t('search_placeholder')}
                      testId="booking-zone-select" />
                  </div>
                </div>

                {formData.zone_id && filteredHotelOpts.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 uppercase tracking-wider">{t('book_hotel')}</label>
                    <SearchableSelect options={filteredHotelOpts} value={formData.hotel_id}
                      onValueChange={(v) => setFormData({ ...formData, hotel_id: v })}
                      placeholder={t('book_select_hotel')} searchPlaceholder={t('search_placeholder')}
                      testId="booking-hotel-select" />
                  </div>
                )}
              </>
            )}

            {/* Excursion Fields */}
            {reservationType === 'excursion' && (
              <div className="space-y-2">
                <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-orange-400" />{t('book_select_exc')} *
                </label>
                <Select value={formData.excursion_id} onValueChange={(v) => setFormData({ ...formData, excursion_id: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-12" data-testid="booking-excursion-select">
                    <SelectValue placeholder={t('book_select_exc_ph')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#112240] border-white/10">
                    {excursions.map((exc) => (
                      <SelectItem key={exc.id} value={exc.id} className="text-white hover:bg-white/10">
                        {lang === 'es' ? (exc.name_es || exc.name) : exc.name} - ${exc.price} USD
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Date, Time, Passengers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-cyan-400" />{t('book_date')} *
                </label>
                <Input type="date" required value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-white/5 border-white/10 text-white h-12" data-testid="booking-date-input" />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-orange-400" />{t('book_time')} *
                </label>
                <Input type="time" required value={formData.time} onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="bg-white/5 border-white/10 text-white h-12" data-testid="booking-time-input" />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <Users className="w-4 h-4 text-cyan-400" />{t('book_passengers')} *
                </label>
                <Select value={formData.passengers} onValueChange={(v) => setFormData({ ...formData, passengers: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-12" data-testid="booking-passengers-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#112240] border-white/10">
                    {[1,2,3,4,5,6,7,8,9,10,11,12,15,20,25,30].map((n) => (
                      <SelectItem key={n} value={n.toString()} className="text-white hover:bg-white/10">
                        {n} {n === 1 ? t('passenger') : t('passengers')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Return Date/Time - Only for round trip */}
            {reservationType === 'transfer' && tripType === 'round_trip' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-orange-500/10 border border-orange-500/20 rounded-xl">
                <div className="space-y-2">
                  <label className="text-sm text-orange-400 uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="w-4 h-4" />{t('book_return_date')} *
                  </label>
                  <Input type="date" required value={formData.return_date}
                    onChange={(e) => setFormData({ ...formData, return_date: e.target.value })}
                    className="bg-white/5 border-white/10 text-white h-12"
                    min={formData.date || undefined}
                    data-testid="booking-return-date" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-orange-400 uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="w-4 h-4" />{t('book_return_time')}
                  </label>
                  <Input type="time" value={formData.return_time}
                    onChange={(e) => setFormData({ ...formData, return_time: e.target.value })}
                    className="bg-white/5 border-white/10 text-white h-12"
                    data-testid="booking-return-time" />
                </div>
              </div>
            )}

            {/* Vehicle Selection */}
            <div className="space-y-2">
              <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <Car className="w-4 h-4 text-cyan-400" />{t('book_vehicle')}
              </label>
              <Select value={formData.vehicle_id} onValueChange={(v) => setFormData({ ...formData, vehicle_id: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white h-12" data-testid="booking-vehicle-select">
                  <SelectValue placeholder={t('book_select_vehicle')} />
                </SelectTrigger>
                <SelectContent className="bg-[#112240] border-white/10">
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id} className="text-white hover:bg-white/10">
                      {v.name} ({v.capacity_min}-{v.capacity_max} {t('passengers').toLowerCase()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Price Estimate */}
            {estimatedPrice !== null && (
              <div className="p-4 bg-gradient-to-r from-orange-600/20 to-yellow-500/20 rounded-xl border border-orange-500/30">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-slate-300">{t('book_price_est')}</span>
                    {reservationType === 'transfer' && tripType === 'round_trip' && (
                      <span className="ml-2 text-xs bg-orange-500/30 text-orange-300 px-2 py-0.5 rounded-full">
                        {t('book_round_trip')}
                      </span>
                    )}
                  </div>
                  <span className="text-2xl font-bold text-orange-400">${estimatedPrice} USD</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {reservationType === 'transfer'
                    ? (tripType === 'round_trip' ? t('book_price_roundtrip') : t('book_price_transfer'))
                    : t('book_price_person')}
                </p>
              </div>
            )}

            {/* Comments */}
            <div className="space-y-2">
              <label className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-orange-400" />{t('book_comments')}
              </label>
              <Textarea value={formData.comments} onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                className="bg-white/5 border-white/10 text-white min-h-[100px]"
                placeholder={t('book_flight_num')} data-testid="booking-comments-input" />
            </div>

            {/* Submit */}
            <Button type="submit" disabled={loading}
              className="w-full bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold uppercase tracking-wide hover:brightness-110 transition-all shadow-[0_0_20px_rgba(234,88,12,0.4)] h-14 text-lg"
              data-testid="booking-submit-btn">
              {loading ? t('book_submitting') : (<><Send className="mr-2 w-5 h-5" />{t('book_submit')}</>)}
            </Button>
          </form>
        </div>
      </div>

      {/* WhatsApp Confirmation Modal */}
      <Dialog open={!!whatsappUrl} onOpenChange={() => setWhatsappUrl(null)}>
        <DialogContent className="bg-[#1E293B] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              {lang === 'es' ? '¡Reservación Registrada!' : 'Reservation Registered!'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-center">
            <p className="text-slate-400">
              {lang === 'es' 
                ? 'Tu reserva ha sido guardada. Envíala por WhatsApp para confirmarla con nuestro equipo.'
                : 'Your reservation has been saved. Send it via WhatsApp to confirm with our team.'}
            </p>
            <a
              href={whatsappUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full py-4 px-6 bg-[#25D366] hover:bg-[#1fba59] text-white font-bold text-lg rounded-xl transition-all hover:scale-[1.02] shadow-lg"
              data-testid="whatsapp-confirm-btn"
              onClick={() => {
                setTimeout(() => setWhatsappUrl(null), 500);
              }}
            >
              <MessageSquare className="w-6 h-6" />
              {lang === 'es' ? 'Enviar por WhatsApp' : 'Send via WhatsApp'}
              <ExternalLink className="w-4 h-4" />
            </a>
            <p className="text-xs text-slate-500">
              {lang === 'es' 
                ? 'Se abrirá WhatsApp con todos los detalles de tu reserva'
                : 'WhatsApp will open with all your reservation details'}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
